import ENDPOINT from 'constants/endpoints'
import {ErrorMessage, Formik} from 'formik'
import useApi from 'hooks/useApi'
import cloneDeep from 'lodash/cloneDeep'
import isArray from 'lodash/isArray'
import merge from 'lodash/merge'
import React, {useEffect, useState} from 'react'
import {toast} from 'react-toastify'
import BasicButtonGroup from '../../../../../components/BasicButtonGroup'
import BasicInput from '../../../../../components/BasicInput'
import BasicSubmitButton from '../../../../../components/BasicSubmitButton'
import FormContentLoader from '../../../../../components/FormContentLoader'
import ERROR_FIELD from '../../../../../constants/error-fields'
import ResponseErrorDialog from '../../../../Dialog/components/ResponseErrorDialog'
import {AccountSettingsFormValidationSchema} from '../validate'

const SecuritySection = props => {
  const {sections} = props
  const initData = {
    isFetching: true,
    fields: {
      currentPass: '',
      newPass: '',
      confirmPass: '',
    },
  };

  const api = useApi();
  const [formData, setFormData] = useState(initData);

  useEffect(() => {
    api.get(
      ENDPOINT.FORM.GET.ACCOUNT_SETTINGS,
      {},
      {
        handleError: response => ResponseErrorDialog({ response }),
        handleSuccess: formData => {
          return setFormData({
            ...merge(initData, formData),
            isFetching: false,
          })
        },
      }
    );
  }, []);

  const scrollToError = () => {
    if (document.getElementsByClassName('form__error').length > 0) {
      const elem = document.getElementsByClassName('form__error')[0];
      if (elem.previousElementSibling) {
        elem.previousElementSibling.scrollIntoView({
          behavior: 'smooth',
          block: 'nearest',
          inline: 'start',
        });
      }
    }
  };

  const onSubmit = (values, { resetForm, setSubmitting, setErrors }) => {
    const preparedValues = cloneDeep(values);
    Object.keys(preparedValues).forEach(field => {
      let data = preparedValues[field];
      if (
        preparedValues[field] === null ||
        field === 'changeEmailRequestTo' ||
        field === 'EmailAddress'
      ) {
        delete preparedValues[field];
      } else if (isArray(data) && data.length === 0) {
        delete preparedValues[field];
      }
    });
    const apiCall = new Promise((resolve, reject) => {
      api.post(ENDPOINT.FORM.SAVE.ACCOUNT_SETTINGS, preparedValues, {
        handleError: response => {
          reject(response)
          onSubmitError(response, setErrors)
        },
        handleSuccess: () => {
          resolve()
          // BasicDialog({ bodyRender: <p>Your account data has been updated.</p> });
          resetForm({ ...values, currentPass: '', newPass: '', confirmPass: '' });
        },
        handleComplete: () => setSubmitting(false),
      });
    });
    toast.promise(apiCall, {
      pending: {
        render(){
          return <p>Saving...</p>
        },
        icon: false,
      },
      success: {
        render(){
          return <p>Your account data has been updated.</p>
        },
        // other options
        icon: "🟢",
      },
      error: {
        render({data}){
          const {
            data: { fields },
          } = data;
          if (fields && typeof setErrors === 'function') {
            const obj = {};
            for (let [key, value] of Object.entries(fields)) {
              const error = isArray(value) ? value[0] : value;
              obj[key] = ERROR_FIELD[error] || `${error}`;
            }
            return Object.values(obj).join('<br />')
          } else {
            // When the promise reject, data will contains the error
            return `${data.status}`
          }
        }
      }
    });

  };

  const onSubmitError = (response, setErrors) => {
    const {
      data: { fields },
    } = response;
    if (fields && typeof setErrors === 'function') {
      const obj = {};
      for (let [key, value] of Object.entries(fields)) {
        const error = isArray(value) ? value[0] : value;
        obj[key] = ERROR_FIELD[error] || `${error}`;
      }
      setErrors(obj);
      scrollToError();
    } else {
      ResponseErrorDialog({ response });
    }
  };

  return (
    <Formik
      enableReinitialize
      initialValues={formData.fields}
      validationSchema={AccountSettingsFormValidationSchema}
      onSubmit={onSubmit}
    >
      {({
          isValidating,
          values,
          handleChange,
          handleSubmit,
          isSubmitting,
          errors,
          touched,
          handleBlur,
          setFieldValue,
          setFieldTouched,
          submitForm,
          setSubmitting,
          resetForm,
          setErrors
        }) => (
        <form
          onSubmit={e => {
            e.preventDefault();
            submitForm().then(() =>
              isValidating ? scrollToError() : null
            );
          }}
          className="form__content"
          autoComplete="off"
        >
          <div className="form__header"><a name="security"></a>
            <h4 className="form__title" >{sections['security'].caption}</h4>
          </div>
          {formData.isFetching && <FormContentLoader />}
          {!formData.isFetching && (
            <>
              <div className="form__group" style={{ margin: '15px 0' }}>
                <BasicInput
                  label="Current Password:"
                  required={true}
                  name="currentPass"
                  inputType="password"
                  content={values.currentPass}
                  placeholder="Type here"
                  controlFunc={handleChange}
                  isValid={
                    (!errors.currentPass && touched.currentPass) ||
                    !touched.currentPass
                  }
                  touchFunc={handleBlur}
                  labelStyles={{ fontWeight: 'bold' }}
                />

                <ErrorMessage
                  name="currentPass"
                  render={message => (
                    <div className="form__error">{message}</div>
                  )}
                />
              </div>
              <div className="form__group" style={{ margin: '15px 0' }}>
                <BasicInput
                  label="New Password:"
                  required={true}
                  name="newPass"
                  inputType="password"
                  content={values.newPass}
                  placeholder="Type here"
                  controlFunc={e => {
                    setFieldTouched('currentPass', true, true);
                    handleChange(e);
                  }}
                  isValid={
                    (!errors.newPass && touched.newPass) || !touched.newPass
                  }
                  touchFunc={handleBlur}
                  labelStyles={{ fontWeight: 'bold' }}
                />

                <ErrorMessage
                  name="newPass"
                  render={message => (
                    <div className="form__error">{message}</div>
                  )}
                />
              </div>
              <div className="form__group" style={{ margin: '15px 0' }}>
                <BasicInput
                  label="Confirm New Password:"
                  required={true}
                  name="confirmPass"
                  inputType="password"
                  content={values.confirmPass}
                  placeholder="Type here"
                  controlFunc={handleChange}
                  isValid={
                    (!errors.confirmPass && touched.confirmPass) ||
                    !touched.confirmPass
                  }
                  touchFunc={handleBlur}
                  labelStyles={{ fontWeight: 'bold' }}
                />

                <ErrorMessage
                  name="confirmPass"
                  render={message => (
                    <div className="form__error">{message}</div>
                  )}
                />
              </div>

              <div className="form__controls">
                <BasicButtonGroup>
                  <BasicSubmitButton
                    disabled={isSubmitting}
                    controlFunc={handleSubmit}
                    btnWidth={200}
                    btnHeight={40}
                    btnFontWeight={500}
                    btnArrow={true}
                    btnTextColor="#27282d"
                    btnText="Update"
                  />
                </BasicButtonGroup>
              </div>
            </>
            )}
        </form>
      )}
    </Formik>
  );
}

export default SecuritySection;
